import type { ReferenceTableHit } from "@/apis/reference-search/types";
import { useRawDsdTableSelection } from "@/hooks/use-raw-dsd-table-selection";
import { referenceSearchQueries } from "@/queries";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { useRef } from "react";

const dartUrl = (rceptNo: string) =>
  `https://dart.fss.or.kr/dsaf001/main.do?rcpNo=${rceptNo}`;

/** 원문 표(sub-chunk) html. 다른 DSD 표와 같이 셀 드래그 선택·복사가 된다. */
export const HitDetail = ({ hit }: { hit: ReferenceTableHit }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useRawDsdTableSelection(containerRef, "reference-search-dsd-selection-style");
  const { data, isLoading, isError } = useQuery(referenceSearchQueries.table(hit.id));

  return (
    <div className="flex flex-col gap-2 border-t border-neutral-200 bg-white px-3 py-3">
      <div className="flex items-center justify-between">
        <span className="caption-1-normal-medium text-label-assistive">
          {hit.companyName} · {hit.noteTitle}
          {data && data.decimalNumber > 0 && ` · 단위 배율 10^${data.decimalNumber}`}
        </span>
        <a
          className="caption-1-normal-medium inline-flex items-center gap-1 text-blue-600 hover:underline"
          href={dartUrl(hit.id.rceptNo)}
          target="_blank"
          rel="noreferrer"
        >
          DART에서 열기 <ExternalLink className="size-3" />
        </a>
      </div>
      {isLoading && <Loader2 className="size-4 animate-spin text-label-assistive" />}
      {isError && (
        <p className="caption-1-normal-medium text-status-negative">
          원문 표를 불러오지 못했습니다.
        </p>
      )}
      {data && (
        <div
          ref={containerRef}
          className="overflow-x-auto text-xs [&_table]:border-collapse [&_td]:border [&_td]:border-neutral-300 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-neutral-300 [&_th]:bg-neutral-100 [&_th]:px-2 [&_th]:py-1"
          dangerouslySetInnerHTML={{ __html: data.html }}
        />
      )}
    </div>
  );
};
